import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { formatNumber } from '../locale.js';
import { Card, CardHeader, StatusPill } from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];

interface Props {
  ingredients: IngredientRow[];
  /** IDs of ingredients produced by the prep system — shown with a "prep" pill. */
  prepIngredientIds?: Set<string>;
  /** Optional button/element rendered in the panel header. */
  action?: React.ReactNode;
}

export function LowStockPanel({ ingredients, prepIngredientIds = new Set(), action }: Props) {
  const { t, i18n } = useTranslation();
  const fmt = (n: number) =>
    formatNumber(n, i18n.language, { maximumFractionDigits: 3 });

  const lowStock = ingredients.filter(
    (i) => i.stock < 0 || (i.low_stock_threshold > 0 && i.stock <= i.low_stock_threshold),
  );

  if (lowStock.length === 0) return null;

  const negative = lowStock.filter((i) => i.stock < 0).length;

  return (
    <Card layout="flush" className="low-stock-panel">
      <CardHeader
        title={
          <span className="low-stock-title">
            <AlertTriangle />
            {t('dashboard.lowStock')}
          </span>
        }
        caption={
          t('dashboard.lowStockCaption', { count: lowStock.length }) +
          (negative ? t('dashboard.lowStockNegative', { count: negative }) : '')
        }
        right={action}
      />
      <div className="low-stock-grid">
        {lowStock.map((i) => {
          const neg = i.stock < 0;
          return (
            <div key={i.id} className={neg ? 'low-stock-tile low-stock-tile--negative' : 'low-stock-tile'}>
              <div className="low-stock-tile-head">
                <span className="low-stock-name">{i.name}</span>
                {prepIngredientIds.has(i.id) && (
                  <StatusPill status="prep" tone="neutral" dot={false}>
                    {t('dashboard.prep')}
                  </StatusPill>
                )}
              </div>
              <span className="low-stock-qty">
                {fmt(i.stock)} {i.unit}
              </span>
              <span className="low-stock-threshold">
                {t('dashboard.threshold', { value: fmt(i.low_stock_threshold) })}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
